import { useAvailabilities } from "@/features/availability/hooks/useAvailabilities";
import { useBusinessWebsite } from "@/features/business-website/hooks/businessWebsiteQueries";
import { useClients } from "@/features/clients/hooks/clientQueries";
import { useServices } from "@/features/services/hooks/serviceQueries";

export interface DashboardSetupScope {
  userId: string;
  businessId: string;
  locationId: string;
}

export type DashboardSetupStepId = "hours" | "services" | "customers" | "website";

export interface DashboardSetupStep {
  id: DashboardSetupStepId;
  complete: boolean;
}

export function useDashboardSetup(scope: DashboardSetupScope) {
  const availability = useAvailabilities(scope);
  const services = useServices();
  const customers = useClients();
  const website = useBusinessWebsite(scope.businessId);
  const error = availability.error || services.error || customers.error || website.error;
  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/;

  const steps: DashboardSetupStep[] | undefined =
    availability.data !== undefined && services.data !== undefined &&
    customers.data !== undefined && website.data !== undefined
      ? [
        {
          id: "hours",
          complete: availability.data.some((item) =>
            item.businessId === scope.businessId && item.locationId === scope.locationId &&
            item.userId === scope.userId && item.isActive && !item.deletedAt &&
            Object.values(item.schedule).some((intervals) => intervals.some((interval) =>
              validTime.test(interval.start) && validTime.test(interval.end) && interval.end > interval.start))),
        },
        {
          id: "services",
          complete: services.data.some((item) => item.businessId === scope.businessId),
        },
        {
          id: "customers",
          complete: customers.data.some((item) => item.businessId === scope.businessId),
        },
        {
          id: "website",
          complete: website.data !== null && website.data.businessId === scope.businessId,
        },
      ] : undefined;

  const retry = () => {
    void availability.refetch();
    void services.refetch();
    void customers.refetch();
    void website.refetch();
  };

  return { steps, error, retry };
}
